/**
 * MZRT x축 축척 — 도메인과 눈금 수를 함께 계산한다.
 *
 * 차트 컴포넌트와 E2E 스펙이 **같은 규칙**을 써야 하므로 순수 모듈로 둔다.
 * (E2E 는 값 → 화면 좌표 변환에 이 도메인을 쓴다. 예전에 스펙이 도메인을
 *  `[0, ceil(maxRT)]` 로 하드코딩하고 있었고, 도메인이 바뀌면 실패하지 않고
 *  조용히 엉뚱한 지점을 찍었다.)
 */

/**
 * 보이는 구간에 맞춘 "딱 떨어지는" 눈금 간격을 고른다 (1·2·5 × 10ⁿ).
 *
 * 도메인을 반올림하지 않으면 recharts 는 `[20.13, 24.87]` 을 그대로 등분해
 * `20.13 · 21.08 · 22.03 …` 같은 눈금을 낸다(실측). 축을 읽을 수 없다.
 */
function niceStep(span: number, targetIntervals: number): number {
  if (!(span > 0)) return 1;
  const raw = span / targetIntervals;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const normalized = raw / magnitude;
  const factor =
    normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return factor * magnitude;
}

/** x 축이 표현할 수 없는 최소 폭 — 점이 하나뿐일 때 도메인이 무너지지 않게 한다. */
const MIN_SPAN: Record<"rt" | "ri", number> = { rt: 1, ri: 50 };

export interface XAxisScale {
  domain: [number, number];
  tickCount: number;
  /** 눈금을 찍을 소수 자릿수. step 에서 나온다. */
  decimals: number;
}

/** step 이 요구하는 소수 자릿수 (0.05 → 2, 0.1 → 1, 1 → 0, 5 → 0). */
function decimalsForStep(step: number): number {
  return Math.max(0, -Math.floor(Math.log10(step)));
}

/**
 * 눈금 문자열.
 *
 * recharts 는 tickFormatter 가 없으면 `min + i*step` 을 **그대로** 찍는다.
 * 부동소수 누적 오차가 그대로 화면에 나와 `8.200000000000001` 같은 눈금이
 * 됐다 (2026-09-06 운영자 제기: "소숫점 이하 7.96000000001 이런식으로 길게
 * 써야하는 꼭 이유가 있을까?"). 자릿수를 step 에 맞춰 고정한다.
 *
 * **값을 반올림해 보여주는 것이 아니다** — 눈금은 우리가 정한 격자 위치이고
 * 그 위치는 애초에 이 자릿수로 딱 떨어진다. 실측값은 커서 칩이 원래 정밀도
 * 그대로 보여준다(compactIntensity.ts 의 같은 원칙).
 */
export function formatXAxisTick(value: number, decimals: number): string {
  return value.toFixed(decimals);
}

/**
 * x축 도메인·눈금 계산 — **보이는 데이터를 따라간다.**
 *
 * 2026-09-06 이전에는 RT 분기가 `[0, Math.ceil(dataMax)]` 였다. `dataMin` 을
 * 버리고 왼쪽을 0 에 고정하므로, TIC 을 브러시해 구간을 좁히면 오른쪽 끝만
 * 따라 움직이고 **점이 오른쪽으로 몰렸다**(운영자 제기). 실측: RT 20~25 에서
 * 점이 폭의 20%, 20~20.5 에서 **2.4%(14px)** — 호버 스냅 반경 20px 보다 좁아
 * 스캔을 하나씩 짚을 수 없는 상태였다.
 *
 * RI 분기(`dataMin - 100`)도 같은 종류였다. 고정 100 패드는 창이 좁아지면
 * 그대로 빈 여백이 된다. 두 분기를 같은 규칙으로 합쳤다.
 *
 * **데이터에서만 계산한다.** 브러시 창을 prop 으로 받지 않는 것이 중요하다 —
 * 커서선·호버 강조·선택 덧그림이 모두 이 함수를 같은 데이터로 다시 호출해
 * 좌표를 맞추기 때문이다(아래 `xDomain` memo). 창을 따로 넘기면 두 좌표계가
 * 조용히 어긋나고, RI 모드에서는 RT 분(分)을 RI 축에 넣는 단위 오류가 된다.
 */
export function computeXAxisScale(
  xAxisType: "rt" | "ri",
  dataMin: number,
  dataMax: number,
): XAxisScale {
  const rawSpan = dataMax - dataMin;
  const span = rawSpan > 0 ? rawSpan : MIN_SPAN[xAxisType];
  const center = rawSpan > 0 ? null : (dataMin + dataMax) / 2;

  const step = niceStep(span, 8);
  const lo = center == null ? dataMin : center - span / 2;
  const hi = center == null ? dataMax : center + span / 2;

  const decimals = decimalsForStep(step);
  // 부동소수 꼬리를 여기서 끊는다. `Math.floor(7.96 / 0.1) * 0.1` 은
  // 7.9000000000000004 이고, 그 값이 그대로 도메인 끝이자 첫 눈금이 된다.
  const snap = (value: number) => Number(value.toFixed(decimals));

  let min = snap(Math.floor(lo / step) * step);
  const max = snap(Math.ceil(hi / step) * step);
  // RT 는 음수가 될 수 없다.
  if (xAxisType === "rt" && min < 0) min = 0;

  // 반올림으로 폭이 0 이 되는 경우(값이 정확히 눈금 위에 하나만 있을 때).
  const domain: [number, number] =
    max > min ? [min, max] : [min, snap(min + step)];

  const tickCount = Math.round((domain[1] - domain[0]) / step) + 1;
  return { domain, tickCount, decimals };
}
